/**
 * Os Super Admins (ADR-0025): quem tem o papel que faz tudo, em todas as
 * organizações, e o lugar de dar e tirar esse papel.
 *
 * Só aparece para Super Admin, e só ele concede: nem o administrador de uma
 * organização, nem a operação da Cliqfy. Ninguém tira o papel de si mesmo — o
 * botão não aparece na própria linha, e a API recusa do mesmo jeito.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { SuperAdmin } from '@cliqcrm/contracts';
import { Avatar, Botao, CabecalhoDeTela, Campo } from '@/shared/ui';
import { chaves } from '@/shared/api/chaves';
import { useApi, useSuperAdmins } from '@/shared/api/consultas';
import { useInvalidar } from '@/shared/api/provedor';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { useSessao } from '@/shared/sessao/contexto';
import { Carregando, Erro } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import './super-admins.css';

export function TelaDeSuperAdmins(): ReactNode {
  const consulta = useSuperAdmins();
  const sessao = useSessao();
  const eu = sessao.estado.nome === 'autenticado' ? sessao.estado.me.usuario.id : null;

  if (consulta.error && !consulta.data) {
    return <Erro erro={consulta.error} aoRepetir={() => void consulta.refetch()} />;
  }
  if (!consulta.data) return <Carregando forma="lista" linhas={4} />;

  const { itens } = consulta.data;

  return (
    <div className="super-admins">
      <CabecalhoDeTela
        titulo="Super Admins"
        contagem={String(itens.length)}
        descricaoDaContagem="Com o papel hoje"
        apoio="Quem faz tudo no CliqCRM, em todas as organizações. Cada entrada numa organização fica na trilha dela."
      />
      <div className="super-admins__corpo">
        <Conceder />
        <ul className="super-admins__lista" aria-label="Super Admins">
          {itens.map((superAdmin) => (
            <Linha
              key={superAdmin.pessoa.id}
              superAdmin={superAdmin}
              souEu={superAdmin.pessoa.id === eu}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

/** Dar o papel pelo e-mail de uma conta que já existe. */
function Conceder(): ReactNode {
  const api = useApi();
  const invalidar = useInvalidar();
  const [email, definirEmail] = useState('');

  const conceder = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      return api.concederSuperAdmin({ corpo: { email: email.trim() } });
    },
    onSuccess: () => {
      definirEmail('');
      invalidar([chaves.superAdmins()]);
    },
  });

  return (
    <form
      className="super-admins__conceder"
      onSubmit={(evento: FormEvent) => {
        evento.preventDefault();
        conceder.mutate();
      }}
    >
      {conceder.error != null && (
        <Faixa tom="bloqueado">
          {recadoDeErro(conceder.error, { generica: 'Não foi possível dar o papel.' })}
        </Faixa>
      )}
      <Campo
        rotulo="Dar o papel de Super Admin"
        type="email"
        placeholder="email@empresa.com.br"
        value={email}
        onChange={(e) => definirEmail(e.target.value)}
        ajuda="A pessoa precisa já ter conta no CliqCRM, convidada para alguma organização."
      />
      <Botao
        type="submit"
        variante="primario"
        desabilitado={email.trim() === '' || conceder.isPending}
        motivo={email.trim() === '' ? 'Escreva o e-mail da pessoa.' : 'Dando o papel…'}
      >
        {conceder.isPending ? 'Dando o papel…' : 'Dar o papel'}
      </Botao>
    </form>
  );
}

/** Uma pessoa com o papel: quem é, desde quando, quem deu, e tirar. */
function Linha({ superAdmin, souEu }: { superAdmin: SuperAdmin; souEu: boolean }): ReactNode {
  const api = useApi();
  const invalidar = useInvalidar();
  const [confirmando, definirConfirmando] = useState(false);

  const revogar = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      return api.revogarSuperAdmin({ caminho: { pessoaId: superAdmin.pessoa.id } });
    },
    onSuccess: () => invalidar([chaves.superAdmins()]),
  });

  return (
    <li className="super-admins__item">
      <Avatar nome={superAdmin.pessoa.nome} tamanho={36} />
      <div className="super-admins__quem">
        <span className="super-admins__nome">
          {superAdmin.pessoa.nome}
          {souEu && <span className="super-admins__voce"> (você)</span>}
        </span>
        <span className="super-admins__email">{superAdmin.pessoa.email}</span>
        <span className="super-admins__desde">
          Desde {data(superAdmin.concedido_em)}
          {superAdmin.concedido_por ? `, por ${superAdmin.concedido_por.nome}` : ', o primeiro'}
        </span>
      </div>

      {revogar.error != null && (
        <Faixa tom="bloqueado">
          {recadoDeErro(revogar.error, { generica: 'Não foi possível tirar o papel.' })}
        </Faixa>
      )}

      {/* Na própria linha não há botão: o papel se tira por outro Super Admin. */}
      {!souEu &&
        (confirmando ? (
          <div className="super-admins__acoes">
            <Botao
              variante="perigo"
              desabilitado={revogar.isPending}
              motivo="Tirando o papel…"
              onClick={() => revogar.mutate()}
            >
              {revogar.isPending ? 'Tirando…' : 'Tirar o papel'}
            </Botao>
            <Botao variante="fantasma" onClick={() => definirConfirmando(false)}>
              Cancelar
            </Botao>
          </div>
        ) : (
          <div className="super-admins__acoes">
            <Botao variante="secundario" onClick={() => definirConfirmando(true)}>
              Tirar o papel
            </Botao>
          </div>
        ))}
    </li>
  );
}

/** A data no fuso de quem lê. */
function data(instante: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(instante));
}
